import BaseModel from '$shared/models/BaseModel';
import DatasetMeta from '$shared/models/DatasetMeta';
import { SimplifiedLicenseType } from '$shared/models/SimplifiedLicenseType';
import { ProviderProfileImportSource } from '$shared/models/ProviderProfileImportSource';
import type { IDatasetMeta } from '$shared/interfaces/api/IDatasetMeta';
import type { IVerificationMetadata } from '$shared/interfaces/api/IVerificationMetadata';
import type { IProviderProfileImportSource } from '$shared/interfaces/api/IProviderProfileImportSource';
import type { ISimplifiedLicenseType } from '$shared/interfaces/api/ISimplifiedLicenseType';
import { DatasetTag } from '$shared/constants/DatasetTag';
import type ProviderProfileSource from '$provider/models/ProviderProfileSource';
import { DatasetSourceName } from '$shared/constants/DatasetSourceName';

export class VerificationMetadata extends BaseModel<IVerificationMetadata> {
  declare public datasetModels: DatasetMeta[];
  declare public simplifiedLicenseTypeModels: SimplifiedLicenseType[];
  declare public providerProfileImportSources: ProviderProfileImportSource[];

  protected override transformResponse(): void {
    this.datasetModels =
      this.response?.datasetModels?.map((item: IDatasetMeta) => new DatasetMeta(item)) || [];
    this.simplifiedLicenseTypeModels =
      this.response?.simplifiedLicenseTypeModels?.map(
        (item: ISimplifiedLicenseType) => new SimplifiedLicenseType(item)
      ) || [];
    this.providerProfileImportSources =
      this.response?.providerProfileImportSources?.map(
        (item: IProviderProfileImportSource) => new ProviderProfileImportSource(item)
      ) || [];
  }

  public get medicaidDatasets(): DatasetMeta[] {
    return this.getDatasetsMetadata(DatasetTag.MedicaidEnrollment);
  }

  public getTypesByTag(tag: DatasetTag): string[] {
    const metaData: DatasetMeta[] = this.getDatasetsMetadata(tag);
    return metaData.map((item: DatasetMeta) => item.type);
  }

  public getDatasetsMetadata(tag?: DatasetTag, datasets?: string[]): DatasetMeta[] {
    return (
      this.datasetModels?.filter((item: DatasetMeta) => {
        if (tag) return item.tags.includes(tag);
        if (Array.isArray(datasets)) return datasets.includes(item.type);
        return true;
      }) || []
    );
  }

  public getDatasetSourceName(type: string): string {
    if (!type) return '';

    const metadata: DatasetMeta | null =
      this.datasetModels?.find((item: DatasetMeta) => item?.type === type) || null;

    return DatasetSourceName[type] || metadata?.name || '';
  }

  public getDatasetMetadata(dataset: string): DatasetMeta | undefined {
    return this.datasetModels?.find((item: DatasetMeta) => item.type === dataset);
  }

  public get SanctionDataset(): string[] {
    return this.getTypesByTag(DatasetTag.SanctionsExclusions) || [];
  }

  public getProfileImportSource(
    source: ProviderProfileSource
  ): ProviderProfileImportSource | undefined {
    return this.providerProfileImportSources?.find((item) => item.source === source);
  }

  public getBoardDatasetMetadata(boardCertType: string): DatasetMeta | undefined {
    return this.datasetModels?.find(
      (item: DatasetMeta) =>
        item.tags.includes(DatasetTag.BoardCertifications) &&
        item.type.toLowerCase() === boardCertType.toLowerCase()
    );
  }

  public getLicenseTypes(): SimplifiedLicenseType[] {
    return this.simplifiedLicenseTypeModels || [];
  }

  public checkDatasetHasPassThroughFee(dataset: string): boolean {
    return !!this.getDatasetMetadata(dataset)?.hasPassThroughFee;
  }
}
